import { useEffect, useState, useMemo } from 'react'
import { callFetchHierarchyData } from '../../hooks/useHierarchyData'
import defaultDates from '../../utils/defaultDates'
import toMillis from '../../helpers/dataHelpers/matrixDataHelpers/toMillis'
import StackedBarGraph from '../Visualisations/outputs/StackedBarGraph'
import { db } from '../../firebase/config'

// AI-CONTEXT: See docs/features/api-data-fetching.md

const getOutputTitle = (() => {
  const cache = new Map()
  return async (outputID) => {
    if (cache.has(outputID)) return cache.get(outputID)
    const snap = await db.collection('Outputs').where('ID', '==', outputID).get()
    const title = snap.empty ? 'Unknown Output' : (snap.docs[0].data().Title || 'Unknown Output')
    cache.set(outputID, title)
    return title
  }
})()

const CombinedOutputData = ({ outputIds = [], startDate, endDate, graphType }) => {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), [])
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw])
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw])
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs])
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs])

  const transformData = (rawArray) => {
    const grouped = rawArray.reduce((acc, doc) => {
      const ts = doc.Timestamp?.toDate
        ? doc.Timestamp.toDate()
        : typeof doc.Timestamp === 'number'
          ? new Date(doc.Timestamp)
          : typeof doc.Timestamp?._seconds === 'number'
            ? new Date(doc.Timestamp._seconds * 1000)
            : new Date(doc.Timestamp)
      const dateString = ts.toLocaleDateString('nl-NL')
      const title = doc.OutputTitle
      const value = typeof doc.Result === 'number' ? doc.Result : 0

      if (!acc[dateString]) acc[dateString] = { name: dateString }
      acc[dateString][title] = (acc[dateString][title] || 0) + value
      return acc
    }, {})

    const array = Object.values(grouped)
    array.sort((a, b) => new Date(a.name.split('-').reverse().join('-')) - new Date(b.name.split('-').reverse().join('-')))
    return array
  }

  const fetchData = async () => {
    if (outputIds.length === 0) {
      setData([])
      setIsLoading(false)
      return
    }

    try {
      const allShapes = await Promise.all(
        outputIds.map(async outputId => {
          const { records } = await callFetchHierarchyData({
            syncType: 'OutputEvent',
            id: outputId,
            startDate: startMs,
            endDate: endMs,
          })

          // Enrich each record with its Output title
          const title = await getOutputTitle(outputId)
          const withTitles = records.map(rec => ({ ...rec, OutputTitle: title }))

          return transformData(withTitles)
        })
      )

      const merged = {}
      allShapes.forEach(shapeArr => {
        shapeArr.forEach(item => {
          if (!merged[item.name]) merged[item.name] = { name: item.name }
          Object.keys(item).forEach(key => {
            if (key === 'name') return
            merged[item.name][key] = (merged[item.name][key] || 0) + item[key]
          })
        })
      })

      const result = Object.values(merged).sort((a, b) =>
        new Date(a.name.split('-').reverse().join('-')) -
        new Date(b.name.split('-').reverse().join('-'))
      )

      setData(result)
    } catch (err) {
      console.error('Error fetching combined output data:', err)
      setData([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [outputIds, startMs, endMs, graphType])

  if (isLoading) return <div>Loading…</div>
  if (data.length === 0) return <div>No data for selected outputs / dates.</div>

  return (
    <StackedBarGraph
      data={data}
      graphType={graphType}
    />
  )
}

export default CombinedOutputData
